import { useEffect, useState, useMemo, useRef, useCallback } from "react";

export const useLatestRef = (obj) => {
  const ref = useRef(obj);
  ref.current = obj;
  return ref;
};

export const useScrollEvent = (scrollEvent) => {
  const eventRef = useLatestRef(scrollEvent);
  useEffect(() => {
    const event = () => {
      eventRef.current?.();
    };
    event();
    window.addEventListener("scroll", event);

    return () => {
      window.removeEventListener("scroll", event);
    };
  }, [eventRef]);
};

export const useNavScrollHide = (start = 500) => {
  const [visible, setVisible] = useState(true);
  const prevScrollTopRef = useRef();

  useScrollEvent(() => {
    const scrollTop = window.scrollY;
    const prevTop = prevScrollTopRef.current;
    if (scrollTop <= start) {
      setVisible(true);
      return;
    }
    if (prevTop >= 0) {
      setVisible(scrollTop < prevTop);
    }
    prevScrollTopRef.current = scrollTop;
  });

  return visible;
};

export const useValuedState = (value, calc = (x) => x) => {
  const [v, setV] = useState(calc(value));
  const calcRef = useLatestRef(calc);

  useEffect(() => {
    if (v === undefined && value !== undefined) {
      setV(calcRef.current(value));
    }
  }, [v, value, calcRef]);

  return [v, setV];
};

export const useObserve = (ratio = 0) => {
  const threshold = useMemo(() => {
    const result = [0];
    if (ratio > 0) {
      result.push(ratio);
    }
    if (!result.includes(1)) {
      result.push(1);
    }
    return result;
  }, [ratio]);
  const lastRatioRef = useRef();
  const [open, setOpen] = useState(false);
  const ioRef = useRef(null);
  const refCallback = useCallback((node) => {
    if (node) {
      if (!ioRef.current) {
        ioRef.current = new IntersectionObserver(
          (entries) => {
            const curRatio = entries[0].intersectionRatio;
            if (lastRatioRef.current === undefined) {
              lastRatioRef.current = curRatio;
              setOpen((x) => curRatio > ratio);
              return;
            }
            if (curRatio > lastRatioRef.current) {
              setOpen((x) => curRatio > ratio);
            } else if (curRatio === 0) {
              setOpen(false);
            }
            lastRatioRef.current = curRatio;
          },
          { threshold }
        );
      }
      ioRef.current.observe(node);
    } else {
      ioRef.current?.disconnect();
      ioRef.current = null;
    }
  }, []);
  return {
    open,
    setOpen,
    ref: refCallback,
  };
};
